import OSS from "ali-oss";
import axios from 'axios';
import Cookies from 'js-cookie';

// 授权请求使用独立实例，避免通用拦截器记录凭证或引入循环依赖。
const baseURL = window.location.href.indexOf('localhost') !== -1 ? '' : '/api'
const http = axios.create({ baseURL, timeout: 10000 })
const REFRESH_MARGIN = 5 * 60 * 1000
let cachedAuthorization = null
let pendingAuthorization = null

/**
 * 读取当前请求身份；收银台仅使用其令牌，登录商户沿用现有请求头约定。
 * 身份标识只用于内存缓存隔离，上传目录始终由后端授权结果决定。
 */
function getAuthorizationContext(cashier) {
  const headers = {
    type: '1',
    client: 'client',
    language: localStorage.getItem('lang') === 'cn' ? 'zh' : 'en'
  }
  if (cashier) {
    const cashierToken = sessionStorage.getItem('cashierToken')
    if (!cashierToken) {
      throw new Error('收银台授权已失效，请重新验证收款码')
    }
    headers.cashierToken = cashierToken
    return { key: JSON.stringify(['cashier', cashierToken]), url: '/cashier/ossToken', headers }
  }
  const satoken = localStorage.getItem('satoken')
  const token = Cookies.get('token') || ''
  if (!satoken && !token) {
    throw new Error('登录已失效，请重新登录后操作文件')
  }
  const login = JSON.parse(sessionStorage.getItem('dataLogin') || 'null')
  const merchantNo = login && login.userInfo ? login.userInfo.merchantNo || '' : ''
  headers.satoken = satoken
  headers.merchantNo = merchantNo
  if (token) {
    headers.token = token
  }
  // 同一路径显式选择 PC 读写凭证；不带 pc 参数的旧客户端仍保留原加密上传契约。
  return { key: JSON.stringify(['merchant', satoken, token, merchantNo]), url: '/oss/token', params: { pc: true }, headers }
}

/**
 * 获取后端临时凭证并验证完整性；失败时直接中止，禁止回退到长期密钥或默认商户。
 */
async function requestAuthorization(context, cashier) {
  const response = await http.get(context.url, { headers: context.headers, params: context.params })
  // 请求期间切换商户或收银台时，旧请求不得写入新身份的凭证缓存。
  if (getAuthorizationContext(cashier).key !== context.key) {
    throw new Error('当前操作身份已变更，请重新操作文件')
  }
  const result = response.data
  if (!result || result.code !== '0000' || !result.data) {
    throw new Error((result && (result.message || result.msg)) || '获取文件临时授权失败')
  }
  const authorization = result.data
  const expiresAt = Date.parse(authorization.expiration)
  if (!authorization.accessKeyId || !authorization.accessKeySecret || !authorization.securityToken ||
      !authorization.bucket || !authorization.region || !Number.isFinite(expiresAt) || expiresAt <= Date.now() + REFRESH_MARGIN ||
      !/^bj\/file\/[A-Za-z0-9_-]+\/$/.test(authorization.uploadPrefix)) {
    throw new Error('文件临时授权无效，请联系管理员')
  }
  return { ...authorization, expiresAt }
}

/**
 * 获取当前身份的 OSS 实例及上传目录，凭证仅缓存在内存，并合并同身份的并发申请。
 * 临近过期时重新申请；分片上传期间由 SDK 刷新同身份凭证。
 */
export async function getOssAuthorization(cashier = false) {
  const context = getAuthorizationContext(cashier)
  if (cachedAuthorization && cachedAuthorization.key === context.key && cachedAuthorization.expiresAt > Date.now() + REFRESH_MARGIN) {
    return cachedAuthorization
  }
  if (pendingAuthorization && pendingAuthorization.key === context.key) {
    return pendingAuthorization.promise
  }
  const pending = { key: context.key }
  pending.promise = (async () => {
    const token = await requestAuthorization(context, cashier)
    const authorization = {
      key: context.key,
      uploadPrefix: token.uploadPrefix,
      expiresAt: token.expiresAt,
      client: null
    }
    authorization.client = new OSS({
      endpoint: 'oss-accelerate.aliyuncs.com',
      region: token.region,
      bucket: token.bucket,
      secure: true,
      accessKeyId: token.accessKeyId,
      accessKeySecret: token.accessKeySecret,
      stsToken: token.securityToken,
      refreshSTSTokenInterval: 5 * 60 * 1000,
      /** 上传期间刷新临时凭证，确保身份和授权目录不发生变化。 */
      refreshSTSToken: async () => {
        if (getAuthorizationContext(cashier).key !== context.key) {
          throw new Error('当前操作身份已变更，文件上传已停止')
        }
        const refreshed = await requestAuthorization(context, cashier)
        if (refreshed.uploadPrefix !== authorization.uploadPrefix || refreshed.bucket !== token.bucket || refreshed.region !== token.region) {
          throw new Error('文件上传授权目录已变更，请重新上传')
        }
        authorization.expiresAt = refreshed.expiresAt
        return {
          accessKeyId: refreshed.accessKeyId,
          accessKeySecret: refreshed.accessKeySecret,
          stsToken: refreshed.securityToken
        }
      }
    })
    cachedAuthorization = authorization
    return authorization
  })()
  pendingAuthorization = pending
  try {
    return await pending.promise
  } finally {
    if (pendingAuthorization === pending) {
      pendingAuthorization = null
    }
  }
}

/**
 * 生成随机uuid
 */
export const getFileNameUUID = () => {
  function rx() {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)
  }
  return `${+new Date()}_${rx()}${rx()}`
}

